import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight, PenTool, Layers, Eye, SlidersHorizontal, BadgeCheck } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { COMPANY, IMAGES, SERVICES, PORTFOLIO, PRINCIPLES } from "@/data/content";

const PRINCIPLE_ICONS = [BadgeCheck, PenTool, Layers, Eye, SlidersHorizontal];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, -56]);
  const ySecondary = useTransform(scrollYProgress, [0, 1], [0, 64]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative bg-white pt-36 sm:pt-44 pb-16 sm:pb-24 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "linear-gradient(to right, rgba(184,154,99,0.06) 1px, transparent 1px)",
          backgroundSize: "clamp(80px, 12vw, 160px) 100%",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="eyebrow mb-7 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block w-8 h-px bg-gold" aria-hidden="true" />
            UK Creative Design Agency
          </motion.p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[1.08]" data-testid="hero-headline">
            <MaskedLine delay={0.2}>Design That</MaskedLine>
            <MaskedLine delay={0.32}>Defines Your</MaskedLine>
            <MaskedLine delay={0.44} className="italic text-gold-deep">Brand.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-charcoal-mute leading-relaxed"
            data-testid="hero-subtext"
          >
            SSDE LTD delivers creative design solutions that help businesses build
            distinctive identities, communicate with confidence, and establish a
            stronger visual presence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/contact" className="btn-gold" data-testid="hero-discuss-project-button">
              Discuss Your Project
              <ArrowUpRight size={16} strokeWidth={1.8} />
            </Link>
            <Link to="/services" className="btn-outline" data-testid="hero-explore-services-button">
              Explore Our Services
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-charcoal/45"
          >
            Ilford, United Kingdom — Serving clients across the UK
          </motion.p>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35 }}
            className="relative"
          >
            <motion.figure style={{ y: yMain }} className="relative z-10 w-[78%] border border-line shadow-[0_30px_70px_-30px_rgba(37,40,45,0.28)]">
              <img
                src={IMAGES.heroMain}
                alt="Premium brand identity stationery suite designed on a warm neutral surface"
                className="w-full aspect-[4/5] object-cover"
                loading="eager"
                data-testid="hero-image-main"
              />
            </motion.figure>
            <motion.figure
              style={{ y: ySecondary }}
              className="absolute -bottom-10 right-0 z-20 w-[46%] border-4 border-white shadow-[0_24px_60px_-24px_rgba(37,40,45,0.3)]"
            >
              <img
                src={IMAGES.heroSecondary}
                alt="Elegant typographic logo design on printed collateral"
                className="w-full aspect-square object-cover"
                loading="eager"
                data-testid="hero-image-secondary"
              />
            </motion.figure>
            <div className="absolute -top-6 -right-3 sm:-right-6 z-0 w-40 h-40 border border-gold/40" aria-hidden="true" />
            <div className="absolute -left-4 bottom-8 z-30 bg-white border border-line px-5 py-4 shadow-[0_16px_44px_-20px_rgba(37,40,45,0.25)]" data-testid="hero-badge-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold-deep">Specialised</p>
              <p className="font-serif text-lg text-charcoal mt-1">Design Activities</p>
              <span className="block w-8 h-px bg-gold my-3" aria-hidden="true" />
              <p className="text-xs text-charcoal-mute leading-relaxed max-w-[180px]">
                A UK studio for brand, print &amp; digital design.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ServicesOverview = () => (
  <section data-testid="services-overview" className="bg-white py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-14">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow mb-4">What We Do</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-charcoal leading-[1.15]">
              Creative Solutions. Strategic Design.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-5">
          <p className="text-sm sm:text-base text-charcoal-mute leading-relaxed lg:text-right">
            We provide specialised design services that help individuals, start-ups
            and established companies present their brands professionally and
            consistently.
          </p>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 4) * 0.07}>
            <Link
              to={`/services#${s.slug}`}
              data-testid={`services-card-${s.slug}`}
              className="group flex flex-col h-full bg-white p-7 transition-colors duration-300 hover:bg-offwhite"
            >
              <span className="font-mono text-[11px] tracking-[0.2em] text-gold-deep">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-5 font-serif text-xl text-charcoal leading-snug">{s.title}</h3>
              <p className="mt-3 text-sm text-charcoal-mute leading-relaxed flex-1">{s.short}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-[13px] font-medium text-charcoal group-hover:text-gold-deep transition-colors duration-300">
                Explore
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedWork = () => {
  const featured = [PORTFOLIO[0], PORTFOLIO[3], PORTFOLIO[5]];
  return (
    <section data-testid="featured-work" className="bg-ivory py-20 sm:py-28 border-y border-line">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="eyebrow mb-4">Portfolio</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-charcoal leading-[1.15]">
              Design in Focus.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/portfolio" className="btn-outline" data-testid="featured-view-portfolio-button">
              View Our Portfolio
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7">
            <FeaturedCard item={featured[0]} tall />
          </Reveal>
          <div className="lg:col-span-5 grid grid-rows-2 gap-6">
            <Reveal delay={0.1}>
              <FeaturedCard item={featured[1]} />
            </Reveal>
            <Reveal delay={0.18}>
              <FeaturedCard item={featured[2]} />
            </Reveal>
          </div>
        </div>
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/45">
          Concept projects shown for illustrative purposes
        </p>
      </div>
    </section>
  );
};

const FeaturedCard = ({ item, tall }) => (
  <Link
    to={`/portfolio/${item.id}`}
    data-testid={`featured-card-${item.id}`}
    className="group relative block overflow-hidden bg-white border border-line h-full"
  >
    <img
      src={item.image}
      alt={`${item.title} — ${item.category} concept by SSDE LTD`}
      className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] ${tall ? "aspect-[4/3] lg:aspect-auto lg:h-full" : "aspect-[16/9] lg:aspect-auto lg:h-full"}`}
      loading="lazy"
    />
    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white/85 to-transparent pt-16 pb-5 px-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-deep">{item.category} · Concept</p>
      <p className="font-serif text-xl text-charcoal mt-1 flex items-center gap-2">
        {item.title}
        <ArrowUpRight size={16} className="text-gold-deep opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
      </p>
    </div>
  </Link>
);

const WhyChoose = () => (
  <section data-testid="why-choose" className="bg-white py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-2xl mb-14">
        <p className="eyebrow mb-4">Why Choose SSDE LTD</p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-charcoal leading-[1.15]">
          Thoughtful Design. Lasting Impressions.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {PRINCIPLES.map((p, i) => {
          const Icon = PRINCIPLE_ICONS[i];
          return (
            <Reveal key={p.title} delay={i * 0.07}>
              <div
                data-testid={`principle-${i + 1}`}
                className="group h-full border border-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_20px_50px_-24px_rgba(37,40,45,0.22)]"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 border border-gold/40 bg-ivory text-gold-deep">
                  <Icon size={19} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-serif text-lg text-charcoal leading-snug">{p.title}</h3>
                <p className="mt-3 text-sm text-charcoal-mute leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const CTASection = () => (
  <section data-testid="cta-section" className="bg-offwhite border-t border-line py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="relative bg-white border border-line px-8 py-14 sm:px-16 sm:py-20 text-center overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-[3px] bg-gold" aria-hidden="true" />
          <div className="absolute -bottom-10 -right-10 w-44 h-44 border border-gold/25 rotate-12" aria-hidden="true" />
          <div className="absolute -top-12 -left-12 w-40 h-40 border border-line -rotate-6" aria-hidden="true" />
          <p className="eyebrow mb-5">Start a Project</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-charcoal leading-[1.15]">
            Have a Project in Mind?
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-base text-charcoal-mute leading-relaxed">
            Let's explore how thoughtful design can help bring your brand vision to life.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/contact" className="btn-gold" data-testid="cta-get-in-touch-button">
              Get in Touch
              <ArrowUpRight size={16} strokeWidth={1.8} />
            </Link>
            <a href={`mailto:${COMPANY.email}`} className="text-sm font-medium text-charcoal hover:text-gold-deep transition-colors duration-300 underline underline-offset-4 decoration-line hover:decoration-gold" data-testid="cta-email-link">
              {COMPANY.email}
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  useSEO(
    "SSDE LTD — Creative Design Agency, UK | Branding, Graphic & Web Design",
    "SSDE LTD delivers specialised design services: branding, graphic design, packaging, website layouts and digital content. Based in Ilford, United Kingdom."
  );
  return (
    <>
      <Hero />
      <Marquee />
      <ServicesOverview />
      <FeaturedWork />
      <WhyChoose />
      <CTASection />
    </>
  );
};

export default Home;
